/**
 * src/lib/session-store.js
 *
 * Caches the one getSessionBootstrap() call for the whole app session so the
 * sidebar user chip, top-bar role badge, and Dashboard greeting all share a
 * single network round trip instead of each screen re-fetching it.
 *
 * getSessionBootstrap is the login-503 fix callable (functions/src/auth/
 * session_bootstrap.ts) — it resolves the ENTIRE session (mapping → profile →
 * role terminology → hierarchy) server-side via the Admin SDK in one call,
 * because the browser's real-time Firestore Listen channel against the
 * me-central2 default DB can 503-loop forever while Admin SDK / callable
 * round trips succeed. This is exactly the read this tracer-bullet slice is
 * meant to prove end-to-end.
 */

import { callFn, guardedRead } from './api.js';

let cached = null;

/**
 * @param {Object} [opts]
 * @param {boolean} [opts.force=false] — bypass the cache and refetch
 * @returns {Promise<{data: import('../types').SessionBootstrap|null, timedOut: boolean, error: any}>}
 */
export function getBootstrap(opts = {}) {
  const { force = false } = opts;
  if (!cached || force) {
    cached = guardedRead(callFn('getSessionBootstrap', {}), {
      // 25000ms to match every other read-CF in web-admin/src/screens
      // ("callable reads can cold-start ~8s" — see reports.js/payroll.js
      // etc.). This used to be 9000ms, which routinely timed out on a
      // me-central2 cold start and — combined with the permanent-cache bug
      // fixed below — stranded the whole session behind a fail-open
      // role=null shell for good.
      timeoutMs: 25000,
      label: 'getSessionBootstrap',
    }).then((result) => {
      // Do NOT permanently memoize a FAILED/non-authoritative bootstrap
      // (timed out, transport error, or resolved with no data at all) —
      // every consumer (shell role gate, nav visibility, user chip, tenant
      // logo) shares this one cache, so caching a failure here would strand
      // the whole session behind it forever. Clearing `cached` lets the
      // NEXT getBootstrap() call (e.g. shell.js's bounded auto-retry, or
      // any screen's own retry affordance) genuinely re-issue the network
      // call instead of replaying the same cached failure. A SUCCESSFUL
      // bootstrap (real data, whether or not a profile doc exists) is still
      // memoized exactly as before — the happy path is unchanged.
      if (result.timedOut || result.error || !result.data) {
        cached = null;
      }
      return result;
    });
  }
  return cached;
}

export function resetBootstrapCache() {
  cached = null;
}
